import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';

const TRANSPORTS = [
  { value: 'tls', label: 'TLS/SSL (port 993) — recommended' },
  { value: 'starttls', label: 'STARTTLS (port 143 → upgrade)' },
  { value: 'plain', label: 'Plain (port 143) — NOT recommended' },
];

const PROVIDER_META = {
  gmail: { label: 'Gmail / Google Workspace', icon: '📬' },
  microsoft: { label: 'Microsoft 365 / Outlook', icon: '📧' },
  imap: { label: 'Generic IMAP', icon: '🔌' },
  simulated: { label: 'Simulated', icon: '🧪' },
};

export default function MailboxConnector() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addProvider, setAddProvider] = useState(null);
  const [sp] = useSearchParams();
  const toast = useToast();

  async function reload() {
    try {
      const r = await api.listConnectors();
      setConnectors(r.items || []);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    reload();
    const oauth = sp.get('oauth');
    const oauthError = sp.get('oauth_error');
    const email = sp.get('email');
    if (oauth === 'success') toast.push(`Connected ${email || ''}`);
    if (oauthError) toast.push('OAuth error: ' + oauthError, { variant: 'error' });
    // eslint-disable-next-line
  }, []);

  async function startOAuth(provider) {
    try {
      const { url } = await api.oauthStart(provider);
      window.location.href = url;
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  async function sync(id) {
    try {
      const r = await api.syncConnector(id);
      toast.push(`Synced · ingested ${r.ingested || 0}`);
      reload();
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  async function disconnect(id, label) {
    if (!window.confirm(`Disconnect "${label}"?`)) return;
    try { await api.deleteConnector(id); toast.push('Disconnected'); reload(); }
    catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  async function remove(id, label) {
    if (!window.confirm(`Permanently remove "${label}"? This cannot be undone.`)) return;
    try { await api.deleteConnector(id, true); toast.push('Removed'); reload(); }
    catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  async function saveImap(body) {
    try {
      await api.configureImap(body);
      toast.push('IMAP mailbox connected');
      setAddProvider(null);
      reload();
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  async function testImap(body) {
    try {
      const r = await api.testImap(body);
      if (r.ok) toast.push(`Test OK · ${r.info?.total || 0} messages in ${r.info?.mailbox || 'INBOX'}`);
      else toast.push('Test failed: ' + r.error, { variant: 'error' });
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  const connected = connectors.filter((c) => c.status === 'connected');
  const others = connectors.filter((c) => c.status !== 'connected');

  return (
    <Shell
      title="Email inbox"
      subtitle={`Connect the mailbox(es) where customer RFQs arrive. ${connectors.length} configured.`}
    >
      {loading ? (
        <div className="empty">Loading…</div>
      ) : connectors.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 className="section-label">Connected mailboxes</h3>
          <div className="connector-list">
            {[...connected, ...others].map((c) => (
              <div key={c._id} className={'connector-row ' + (c.status === 'connected' ? 'ok' : c.status === 'error' ? 'err' : 'off')}>
                <div className="connector-row-icon">{PROVIDER_META[c.provider]?.icon || '📨'}</div>
                <div className="connector-row-main">
                  <div className="connector-row-title">
                    {c.label || PROVIDER_META[c.provider]?.label}
                    <span className={'chip ' + (c.status === 'connected' ? 'ready' : c.status === 'error' ? 'risk' : 'muted')} style={{ marginLeft: 8 }}>
                      <span aria-hidden="true">{c.status === 'connected' ? '✓' : c.status === 'error' ? '⚠' : '●'}</span> {c.status}
                    </span>
                  </div>
                  <div className="connector-row-meta">
                    {PROVIDER_META[c.provider]?.label} · {c.displayEmail || '—'}
                    {c.lastSyncAt && <> · last sync {new Date(c.lastSyncAt).toLocaleString()}</>}
                  </div>
                  {c.lastError && <div className="connector-row-err">{c.lastError}</div>}
                </div>
                <div className="connector-row-actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => sync(c._id)}>Sync</button>
                  {c.status === 'connected' && <button className="btn btn-sm btn-ghost" onClick={() => disconnect(c._id, c.label || c.displayEmail)}>Disconnect</button>}
                  {c.status !== 'connected' && <button className="btn btn-sm btn-ghost" onClick={() => remove(c._id, c.label || c.displayEmail)}>Remove</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="section-label">Add a mailbox</h3>
      <div className="connector-grid">
        <ProviderCard
          title="Gmail / Google Workspace"
          description="OAuth via Google. Scopes: read + modify. You can connect multiple Gmail accounts."
          icon="📬"
          actionLabel="Connect Google"
          onAction={() => startOAuth('google')}
          protocols={['OAuth 2.0', 'TLS 1.3']}
        />
        <ProviderCard
          title="Microsoft 365 / Outlook"
          description="OAuth via Microsoft (MSAL v2). Uses Microsoft Graph API — no IMAP required. Works with Outlook.com and enterprise M365."
          icon="📧"
          actionLabel="Connect Microsoft"
          onAction={() => startOAuth('microsoft')}
          protocols={['OAuth 2.0', 'Graph API', 'TLS 1.3']}
        />
        <ProviderCard
          title="Generic IMAP"
          description="Any IMAP server (Zimbra, Dovecot, FastMail, Exchange-IMAP, self-hosted). Use an app-password where possible."
          icon="🔌"
          actionLabel="Configure IMAP"
          onAction={() => setAddProvider('imap')}
          protocols={['TLS', 'STARTTLS', 'Plain (warn)']}
        />
      </div>

      {addProvider === 'imap' && (
        <ImapModal
          onSave={saveImap}
          onTest={testImap}
          onClose={() => setAddProvider(null)}
        />
      )}

      <div className="card" style={{ marginTop: 24 }}>
        <h3 className="card-title">What Rivet reads</h3>
        <ul style={{ lineHeight: 1.7, paddingLeft: 20 }}>
          <li><b>Read-only by default</b> — Rivet pulls unread messages into your Rivet Mailbox.</li>
          <li><b>Nothing is sent</b> from these connectors. Replies go through your regular email client.</li>
          <li><b>Secrets encrypted at rest</b> (AES-256-GCM), redacted in API responses, never logged.</li>
          <li><b>Multiple mailboxes supported</b> — add as many as you need; Rivet polls each independently.</li>
        </ul>
      </div>
    </Shell>
  );
}

function ProviderCard({ title, description, icon, actionLabel, onAction, protocols }) {
  return (
    <div className="connector-card">
      <div className="connector-card-head">
        <div className="connector-icon">{icon}</div>
        <h3>{title}</h3>
      </div>
      <p>{description}</p>
      <div className="connector-protocols">
        {protocols.map((p) => <span key={p} className="chip muted">{p}</span>)}
      </div>
      <div className="connector-card-foot">
        <button className="btn" onClick={onAction}>{actionLabel} →</button>
      </div>
    </div>
  );
}

function ImapModal({ onSave, onTest, onClose }) {
  const [form, setForm] = useState({
    label: '',
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    imapUser: '',
    password: '',
    imapTransport: 'tls',
    imapMailbox: 'INBOX',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ width: 560, background: '#fff', maxHeight: '90vh', overflow: 'auto' }}>
        <h2 className="card-title">Connect IMAP mailbox</h2>
        <label className="field"><span className="label">Label (optional)</span>
          <input className="input" value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="e.g. Boeing RFQs inbox" />
        </label>
        <div className="row">
          <label className="field"><span className="label">Host</span>
            <input className="input" value={form.imapHost} onChange={(e) => set('imapHost', e.target.value)} />
          </label>
          <label className="field"><span className="label">Port</span>
            <input className="input mono" type="number" value={form.imapPort} onChange={(e) => set('imapPort', e.target.value)} />
          </label>
        </div>
        <label className="field"><span className="label">User</span>
          <input className="input" value={form.imapUser} onChange={(e) => set('imapUser', e.target.value)} placeholder="rfq@yourdomain.com" />
        </label>
        <label className="field"><span className="label">Password / app-password</span>
          <input className="input" type="password" value={form.password} onChange={(e) => set('password', e.target.value)} />
        </label>
        <label className="field"><span className="label">Transport</span>
          <select className="select" value={form.imapTransport} onChange={(e) => set('imapTransport', e.target.value)}>
            {TRANSPORTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        {form.imapTransport === 'plain' && (
          <div className="chip risk" style={{ marginBottom: 8 }}>
            ⚠ Plain-text IMAP transmits credentials unencrypted. Use only on trusted networks.
          </div>
        )}
        <label className="field"><span className="label">Mailbox</span>
          <input className="input" value={form.imapMailbox} onChange={(e) => set('imapMailbox', e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={() => onTest(form)} disabled={!form.imapHost || !form.imapUser || !form.password}>Test</button>
          <button className="btn" onClick={() => onSave(form)} disabled={!form.imapHost || !form.imapUser || !form.password}>Connect</button>
        </div>
      </div>
    </div>
  );
}
